'use client';

import { AddOnSlot, LeadingAdornmentSlot, TrailingAdornmentSlot } from '@magicschool/spellbook/components/Textarea/slots';
import { useFieldSizingFallback } from '@magicschool/spellbook/hooks/useFieldSizingFallback';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useImperativeHandle, useRef } from 'react';

export type TextareaProps = Omit<React.ComponentProps<'textarea'>, 'size'> & // size is used for styling, not the default HTML attribute
  Omit<VariantProps<typeof textareaFieldWrapperVariants>, 'rows' | 'invalid'> & {
    /** When `true`, the textarea auto-resizes to fit content. When `rows` is set, auto-resize is disabled and the user can manually drag to resize. Default: `false`. */
    resize?: boolean;
    /** Associates the textarea with an external label via `aria-labelledby` when no `id` is provided. */
    labelId?: string;
    /** Applies error border styling. Pass as a boolean, not the string "true". Pair with `aria-describedby` pointing to an error message element. */
    invalid?: boolean;
    /** `outlined` (default) is the bordered form field; `bare` drops the border, background and padding so the field sits inline inside a surface that already frames it. */
    variant?: 'outlined' | 'bare';
  };

export const textareaFieldWrapperVariants = cva('cursor-text flex items-start justify-start w-full relative', {
  variants: {
    /** The size of the input. */
    size: { md: 'rounded-control-md', lg: 'rounded-control-lg' },
    /** Whether the input is read-only. */
    readOnly: { true: 'cursor-text' },
    /** Whether the input is disabled. */
    disabled: { true: '!cursor-not-allowed' },
    /** Whether the field is invalid */
    invalid: { true: 'border-error-stroke hover:border-error-stroke' },
    /** Whether the field resizes for longer text */
    resize: { true: '', false: '' },
    rows: { true: 'h-auto', false: 'h-auto' },
  },
  compoundVariants: [
    { size: 'md', resize: false, className: '' },
    { size: 'lg', resize: false, className: '' },
    { rows: true, resize: true, className: '' },
    { rows: false, resize: true, className: '' },
    { rows: false, resize: false, className: '' },
  ],
});

const textareaWrapperVariants = cva(
  [
    'flex',
    'flex-col',
    'w-full',
    'sm:min-w-form-field-min',
    'border',
    'text-sm',
    'font-normal',
    'transition-all',
    'outline-none',
    'relative',
    'group/textarea',
  ],
  {
    variants: {
      size: { md: 'rounded-control-md', lg: 'rounded-control-lg' },
      readOnly: {
        true: [
          'border-surface-background-1',
          'bg-surface-background-1',
          'hover:border-surface-background-1',
          'focus-within:border-surface-background-1',
        ],
        false: [
          'bg-surface-background-0',
          'border-border-strong',
          'hover:border-border-strong-hover',
          'focus-within:border-focus-ring',
          'hover:focus-within:border-focus-ring',
        ],
      },
      disabled: {
        true: [
          'cursor-not-allowed',
          'border-disabled-background',
          'hover:border-disabled-background',
          'bg-disabled-background',
          'text-disabled-foreground',
        ],
      },
      invalid: {
        true: [
          /**
           * @TODO [SPE-105] Centralize "invalid"/state color tokens to single utility
           * https://linear.app/magicschool/issue/SPE-105/centralize-invalid-state-color-tokens
           */
          'border-error-stroke',
          'hover:border-error-stroke',
          'focus-within:border-error-stroke',
          'hover:focus-within:border-error-stroke',
        ],
      },
      rows: { true: 'h-fit', false: 'h-fit' },
      /** Whether the field draws its own frame (`outlined`) or sits inside one (`bare`). */
      variant: { outlined: '', bare: '' },
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'border-disabled-background hover:border-disabled-background focus-within:border-disabled-background',
      },
      // Last, so tailwind-merge drops the frame classes the other variants added.
      { variant: 'bare', className: ['border-0', 'bg-transparent', 'rounded-none', 'sm:min-w-0'] },
    ],
  },
);

const textareaFieldVariants = cva(
  [
    'cursor-text',
    'text-default-text-strong',
    'focus-visible:outline-0',
    'w-full',
    'h-auto',
    'bg-transparent',
    'placeholder:text-placeholder',
    'transition-colors',
    'group-hover/textarea:placeholder:text-placeholder-hover',
    'disabled:cursor-not-allowed',
    'disabled:placeholder:text-disabled-foreground',
    'disabled:text-disabled-foreground',
    'scroll-pb-4',
  ],
  {
    variants: {
      size: {
        md: 'pb-2 min-h-16',
        lg: 'pb-[0.71875rem] min-h-20',
      },
      readOnly: {
        true: 'placeholder:text-disabled-foreground',
      },
      hasLeadingAdornment: { true: '' },
      hasTrailingAdornment: { true: '' },
      rows: { true: '', false: '' },
      resize: { true: '', false: '' },
      /** Whether the field draws its own frame (`outlined`) or sits inside one (`bare`). */
      variant: { outlined: '', bare: '' },
    },
    compoundVariants: [
      { size: 'lg', hasLeadingAdornment: false, className: 'ps-3 ' },
      { size: 'md', hasLeadingAdornment: false, className: 'ps-2' },
      { size: 'md', hasTrailingAdornment: false, className: 'pe-2' },
      { size: 'lg', hasTrailingAdornment: false, className: 'pe-3' },
      { size: 'md', hasTrailingAdornment: true, className: 'pt-2 pe-9' }, // padding-end for absolutely positioned trailing adornment
      { size: 'lg', hasTrailingAdornment: true, className: 'pt-[0.71875rem] pe-11' },
      { size: 'md', hasLeadingAdornment: true, className: 'pt-2' },
      { size: 'lg', hasLeadingAdornment: true, className: 'pt-[0.71875rem]' },
      { size: 'md', hasTrailingAdornment: false, hasLeadingAdornment: false, className: 'pt-2' },
      { size: 'lg', hasTrailingAdornment: false, hasLeadingAdornment: false, className: 'pt-[0.71875rem]' },
      { size: 'md', resize: false, className: 'h-16' },
      { size: 'lg', resize: false, className: 'h-20' },
      { rows: true, resize: true, className: 'resize-y max-h-unset' },
      { rows: false, resize: true, className: 'resize-none field-sizing-content max-h-30 overflow-y-auto' },
      { rows: [true, false], resize: false, className: 'resize-none' },
      // Last, so tailwind-merge drops the size padding and minimum height above.
      { variant: 'bare', className: ['p-0', 'min-h-0'] },
    ],
  },
);

const TextareaBase = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      children,
      className,
      disabled = false,
      id,
      labelId,
      readOnly = false,
      invalid = false,
      resize = false,
      size = 'md',
      variant = 'outlined',
      placeholder,
      onChange,
      rows,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLTextAreaElement | null>(null);
    useFieldSizingFallback(ref, { enabled: resize && !rows, value: props.value });

    const { slots } = useSlots(children, {
      leadingAdornment: { Component: LeadingAdornmentSlot, props: { size, ...(disabled && { color: 'disabled' }) } },
      trailingAdornment: { Component: TrailingAdornmentSlot, props: { size, ...(disabled && { color: 'disabled' }) } },
      addOn: { Component: AddOnSlot, props: { size } },
    });

    const focusTextareaFromContainer = (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const textarea = event.currentTarget.querySelector('textarea');
      if (textarea) {
        textarea.focus();
      }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event);
    };

    useImperativeHandle(forwardedRef, () => ref.current!, []);

    return (
      <div
        data-spellbook-name="textarea"
        className={cn(textareaWrapperVariants({ size, disabled, readOnly, invalid, rows: Boolean(rows), variant }), className)}
      >
        <div
          className={cn(textareaFieldWrapperVariants({ disabled, size, readOnly, invalid, resize, rows: Boolean(rows) }))}
          onClick={focusTextareaFromContainer}
        >
          {slots.leadingAdornment}
          <textarea
            onChange={handleOnChange}
            className={cn(
              textareaFieldVariants({
                size,
                readOnly,
                hasLeadingAdornment: Boolean(slots.leadingAdornment),
                hasTrailingAdornment: Boolean(slots.trailingAdornment),
                rows: Boolean(rows),
                resize,
                variant,
              }),
            )}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            rows={rows}
            aria-invalid={invalid ?? undefined}
            aria-readonly={readOnly ?? undefined}
            aria-disabled={disabled ?? undefined}
            ref={ref}
            id={id}
            {...(labelId && !id && { 'aria-labelledby': labelId })}
            {...props}
          />
          {slots.trailingAdornment}
        </div>
        {slots.addOn}
      </div>
    );
  },
);

/**
 * Multi-line text input for forms, supporting auto-resize, adornments, and an add-on region.
 *
 * ### When to use
 * - Multi-line text entry such as feedback forms, comments, descriptions, or prompts
 * - Inputs that benefit from auto-resizing to fit content
 * - Fields that need an add-on region for supplementary controls (e.g., file upload, word count)
 *
 * ### When NOT to use
 * - Single-line text entry: use `TextInput` instead
 * - Rich text or markdown editing: use a rich text editor component instead
 * - Search fields: use `SearchInput` instead
 *
 * ### API
 * - `size`: Input dimensions: `md` (default) for standard forms, `lg` for large viewport-filling inputs. Prefer `md` in most contexts.
 * - `resize`: When `true`, the textarea auto-resizes as the user types. When `rows` is set, auto-resize is disabled and the user can manually drag to resize. Default: `false`.
 * - `rows`: Sets a fixed number of visible text rows. Disables auto-resize behavior.
 * - `variant`: `outlined` (default) for a standalone form field; `bare` drops the frame and padding for a field that sits inline inside a surface that already frames it (a list row, a card)
 * - `invalid`: Applies error border styling. Pair with `aria-describedby` pointing to an error message
 * - `readOnly`: Renders a non-editable textarea with muted styling
 * - `labelId`: Associates the textarea with an external label via `aria-labelledby` when no `id` is provided
 *
 * ### Composition (slots)
 * - `<Textarea.LeadingAdornment>`: Icon positioned at the start of the textarea. Accepts `IconProps`. Only include when adding a visual indicator — not rendered by default.
 * - `<Textarea.TrailingAdornment>`: Icon positioned at the top-end corner (absolutely positioned). Accepts `IconProps`. Only include when adding an action or indicator — not rendered by default.
 * - `<Textarea.AddOn>`: Region below the textarea for supplementary controls (e.g., file upload buttons, word count). Accepts `div` props. Only include when adding toolbar-style controls — not rendered by default.
 *
 * ### Visual states
 * - **Default**: Standard border, white background
 * - **Hover**: Border darkens to `border-hover`
 * - **Focus**: Border changes to `focus-ring`
 * - **Invalid**: Error-colored border, sets `aria-invalid="true"`
 * - **Disabled**: Muted background, `cursor-not-allowed`, sets `aria-disabled="true"`
 * - **Read-only**: Subtle background, sets `aria-readonly="true"`
 *
 * ### Accessibility
 * - MUST have a visible `<Label>` via `Form.Group` or a standalone `<Label htmlFor={id}>`. Use `sr-only` class only when a visible label is not possible.
 * - Error states should pair `invalid` with `aria-describedby` referencing the error message element
 * - When no `id` is set, provide `labelId` to associate with an external label via `aria-labelledby`
 */
export const Textarea = Object.assign(TextareaBase, {
  LeadingAdornment: LeadingAdornmentSlot,
  TrailingAdornment: TrailingAdornmentSlot,
  AddOn: AddOnSlot,
});
